/**
 * FramingGuide ✦ iter199 — host-facing visual framing calibration.
 *
 * The visible layer of the iter199 "composition language" directive.
 * Lives over a profile-driven camera surface, never visible to
 * guests by default. Two host-facing entry points:
 *
 *   1. Persisted toggle in Retune Room ("Composition guide ✦")
 *      — sessionStorage flag survives view changes.
 *   2. Long-press on the camera frame summons the guide for ~6s
 *      regardless of the persisted toggle. The operator's emergency
 *      "I need to see framing right now" gesture.
 *
 * Render contract (per Craig, iter199):
 *
 *   • Corner ticks at the safe-zone bounds — NOT a full rectangle
 *     (that reads as "production tool", not "authored atmosphere").
 *   • A single horizontal hairline at profile.headroomGuide y.
 *   • 25% opacity baseline. Fades in over ~1.4s on first reveal,
 *     holds at full visibility for ~6s, then fades out over another
 *     ~1.4s — total lifecycle ~7.4s. "The guide should teach, not
 *     linger."
 *
 * The guide is purely informational — it never affects the
 * captured frame. The pipeline's safeZone + cropAnchor already
 * compose the export.
 */
import React, { useEffect, useRef, useState } from 'react';

const SESSION_FLAG = 'booth_composition_guide';
const LONGPRESS_MS = 900;
const DISSOLVE_MS = 6_000;
const FADE_IN_MS = 1_400;
const BASE_OPACITY = 0.25;
// iter201 — the "room breathing in" moment. A near-subliminal serif
// italic line near the lower safe zone shown ONLY while the stream
// is settling, capped at BREATH_MAX_MS so it never reads as a
// loader. The breath is intentionally bounded — even if dimensions
// take longer to stabilise, the line disappears at BREATH_MAX_MS
// and the rest of the wait is held in silence.
const BREATH_FADE_MS = 140;
const BREATH_MAX_MS = 350;
const BREATH_OPACITY = 0.18;
const BREATH_IVORY = 'rgba(248,232,200,0.85)';

const isPersistedOn = () => {
  try { return sessionStorage.getItem(SESSION_FLAG) === '1'; }
  catch { return false; }
};

export function setCompositionGuidePersisted(on) {
  try { sessionStorage.setItem(SESSION_FLAG, on ? '1' : '0'); }
  catch { /* no-op */ }
}

/**
 * Lightweight hook so other host-facing surfaces (theme picker /
 * retune room / future "Atmosphere Check") can read & toggle the
 * persisted state without reaching into sessionStorage directly.
 */
export function useCompositionGuideToggle() {
  const [on, setOn] = useState(() => isPersistedOn());
  const toggle = () => {
    const next = !on;
    setOn(next);
    setCompositionGuidePersisted(next);
  };
  return { on, toggle };
}

/**
 * ComposingBreath ✦ iter201 — the "room breathing in" moment.
 *
 * Rendered only while the FramingGuide is in 'waiting' phase. A
 * single serif italic line near the lower safe-zone edge, fades
 * in over 140ms, holds, fades out at BREATH_MAX_MS regardless of
 * whether dimensions have stabilised yet. The breath is
 * intentionally bounded so it never reads as a loader — only as
 * authored pacing.
 *
 * Visual contract:
 *   • opacity 0.18 (less than helper text — almost subliminal)
 *   • serif italic, warm ivory
 *   • positioned at the lower safe-zone edge of the profile
 *   • no animation except opacity fade
 *   • max visible lifecycle 350ms
 */
function ComposingBreath({ profile }) {
  const [opacity, setOpacity] = useState(0);

  useEffect(() => {
    const inT = setTimeout(() => setOpacity(BREATH_OPACITY), 16);
    const outT = setTimeout(() => setOpacity(0), BREATH_MAX_MS - BREATH_FADE_MS);
    return () => { clearTimeout(inT); clearTimeout(outT); };
  }, []);

  const sz = profile?.safeZone || { x: 0.06, y: 0.08, w: 0.88, h: 0.84 };
  // Place the breath just inside the lower safe-zone edge — close
  // enough to feel like it's part of the frame, but not on the
  // bottom border itself (that reads as a caption, not a breath).
  const bottomPct = (1 - (sz.y + sz.h)) * 100 + 6;

  return (
    <div
      data-testid="composing-breath"
      aria-hidden
      style={{
        position: 'absolute',
        left: 0, right: 0,
        bottom: `${bottomPct}%`,
        textAlign: 'center',
        fontFamily: 'Georgia, "Cormorant Garamond", "Times New Roman", serif',
        fontStyle: 'italic',
        fontSize: 13,
        letterSpacing: '0.04em',
        color: BREATH_IVORY,
        opacity,
        transition: `opacity ${BREATH_FADE_MS}ms ease-out`,
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      composing… ✦
    </div>
  );
}

/**
 * <FramingGuide profile gold longPressTarget streamReady /> —
 * render over a camera frame container with position: relative.
 *
 * Props:
 *   profile         — frozen profile from boothCameraProfiles
 *   gold            — accent colour (defaults to a restrained gold)
 *   longPressTarget — ref to the surface that should accept the
 *                     summon gesture (usually the camera frame)
 *   visibleOverride — bypass persisted flag for testing / preview
 *   streamReady     — iter200. The pipeline flips this true only
 *                     after two consecutive rAF ticks report the
 *                     same non-zero videoWidth×videoHeight (i.e.
 *                     iOS/Android stopped renegotiating frame
 *                     geometry). The guide will NOT begin its
 *                     fade-in until streamReady is true — that's
 *                     what kills the one-frame jump when the
 *                     camera dimensions resolve asynchronously.
 *                     Defaults to true so non-pipeline consumers
 *                     (tests, the future /debug route) still work.
 */
export default function FramingGuide({
  profile,
  gold = 'rgba(212,175,94,0.85)',
  longPressTarget,
  visibleOverride,
  streamReady = true,
}) {
  const persisted = isPersistedOn();
  const [summoned, setSummoned] = useState(false);
  const [phase, setPhase] = useState('waiting'); // → 'fading-in' → 'visible' → 'dissolving' → 'hidden'
  const summonTimerRef = useRef(null);
  const dissolveTimerRef = useRef(null);

  const active = visibleOverride === true || persisted || summoned;

  // Long-press gesture on the camera frame — summons the guide for
  // a fresh dissolve cycle even when the persisted toggle is off.
  useEffect(() => {
    const el = longPressTarget?.current;
    if (!el) return undefined;
    let pressStart = 0;
    let timeoutId = null;
    const onDown = () => {
      pressStart = Date.now();
      timeoutId = setTimeout(() => {
        setSummoned(true);
        setPhase('fading-in');
      }, LONGPRESS_MS);
    };
    const onCancel = () => {
      if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
      pressStart = 0;
    };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointerup', onCancel);
    el.addEventListener('pointerleave', onCancel);
    el.addEventListener('pointercancel', onCancel);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointerup', onCancel);
      el.removeEventListener('pointerleave', onCancel);
      el.removeEventListener('pointercancel', onCancel);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [longPressTarget]);

  // Dissolve schedule whenever the guide becomes active AND the
  // stream has settled. The temporal stabilisation contract: we
  // sit in 'waiting' until streamReady flips true — only THEN do
  // we begin the cinematic fade-in. Otherwise the guide's corner
  // ticks would jump once when iOS/Android renegotiates frame
  // dimensions.
  useEffect(() => {
    if (!active) {
      setPhase('hidden');
      return undefined;
    }
    if (!streamReady) {
      setPhase('waiting');
      return undefined;
    }
    setPhase('fading-in');
    const t1 = setTimeout(() => setPhase('visible'), FADE_IN_MS);
    const t2 = setTimeout(() => setPhase('dissolving'), DISSOLVE_MS);
    const t3 = setTimeout(() => {
      setPhase('hidden');
      // Summon is one-shot — persisted toggle re-asserts on next
      // active=true (e.g. surface remount).
      setSummoned(false);
    }, DISSOLVE_MS + FADE_IN_MS);
    summonTimerRef.current = t1;
    dissolveTimerRef.current = t2;
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [active, streamReady]);

  if (!active || phase === 'hidden' || phase === 'waiting') {
    // iter201 — During the 'waiting' phase (stream still settling)
    // render a near-subliminal serif italic breath near the lower
    // safe-zone edge. Capped to BREATH_MAX_MS regardless of how
    // long dimensions take — so it never reads as a loader; only
    // as "the room breathing in". On fast devices the breath is
    // barely perceptible; on slow ones the rest of the wait is
    // held in calm silence.
    if (active && phase === 'waiting') {
      return <ComposingBreath profile={profile} />;
    }
    return null;
  }

  const opacity = phase === 'fading-in' || phase === 'dissolving'
    ? 0
    : BASE_OPACITY;
  const transition = `opacity ${FADE_IN_MS}ms ease-out`;

  const sz = profile.safeZone || { x: 0.06, y: 0.08, w: 0.88, h: 0.84 };
  const headroomY = (profile.headroomGuide ?? 0.40) * 100;

  // Corner ticks — 22px arms drawn with absolute positioning at
  // the four corners of the safe zone (NOT a full outline).
  const tickLen = 22;
  const tickWeight = 1;
  const cornerStyle = {
    position: 'absolute',
    width: tickLen, height: tickLen,
    pointerEvents: 'none',
  };

  return (
    <div
      data-testid="framing-guide"
      data-phase={phase}
      aria-hidden
      style={{
        position: 'absolute',
        left: `${sz.x * 100}%`,
        top: `${sz.y * 100}%`,
        width: `${sz.w * 100}%`,
        height: `${sz.h * 100}%`,
        pointerEvents: 'none',
        opacity, transition,
        zIndex: 4,
      }}
    >
      {/* ── Four corner ticks (top-left, top-right, bottom-left,
          bottom-right). Each tick is two perpendicular hairlines. ─ */}
      <span style={{ ...cornerStyle, top: 0, left: 0 }}>
        <span style={{ position: 'absolute', top: 0, left: 0, width: tickLen, height: tickWeight, background: gold }} />
        <span style={{ position: 'absolute', top: 0, left: 0, width: tickWeight, height: tickLen, background: gold }} />
      </span>
      <span style={{ ...cornerStyle, top: 0, right: 0 }}>
        <span style={{ position: 'absolute', top: 0, right: 0, width: tickLen, height: tickWeight, background: gold }} />
        <span style={{ position: 'absolute', top: 0, right: 0, width: tickWeight, height: tickLen, background: gold }} />
      </span>
      <span style={{ ...cornerStyle, bottom: 0, left: 0 }}>
        <span style={{ position: 'absolute', bottom: 0, left: 0, width: tickLen, height: tickWeight, background: gold }} />
        <span style={{ position: 'absolute', bottom: 0, left: 0, width: tickWeight, height: tickLen, background: gold }} />
      </span>
      <span style={{ ...cornerStyle, bottom: 0, right: 0 }}>
        <span style={{ position: 'absolute', bottom: 0, right: 0, width: tickLen, height: tickWeight, background: gold }} />
        <span style={{ position: 'absolute', bottom: 0, right: 0, width: tickWeight, height: tickLen, background: gold }} />
      </span>

      {/* ── Single horizontal headroom hairline at the profile's
          subject-anchor y. Faded ends so it doesn't read as a
          frame edge. ─────────────────────────────────────────── */}
      <span
        data-testid="framing-guide-headroom"
        aria-hidden
        style={{
          position: 'absolute',
          left: '6%',
          right: '6%',
          top: `${headroomY}%`,
          height: 1,
          background: `linear-gradient(90deg, transparent 0%, ${gold} 25%, ${gold} 75%, transparent 100%)`,
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}
